import { createHOC, createRenderFn } from 'vue-hoc';

var setName = function setName(name) {
  return function (ctor) {
    var target = typeof ctor === 'function' ? ctor.options : ctor;

    target.name = name;

    return ctor;
  };
};

var getName = function getName(ctor) {
  var target = typeof ctor === 'function' ? ctor.options : ctor;

  return target.name || '';
};

var wrapName = function wrapName(name) {
  return function (ctor) {
    var cname = getName(ctor) || 'Anonymous';
    return name + '-' + cname;
  };
};

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var branch = (function (testFn, trueFn, f) {
  return function (ctor) {
    var falseFn = f;
    if (falseFn == null) {
      falseFn = createRenderFn(ctor);
    }
    if ((typeof falseFn === 'undefined' ? 'undefined' : _typeof(falseFn)) === 'object' && typeof falseFn.render === 'function') {
      falseFn = falseFn.render;
    }
    if ((typeof trueFn === 'undefined' ? 'undefined' : _typeof(trueFn)) === 'object' && typeof trueFn.render === 'function') {
      trueFn = trueFn.render;
    }

    return createHOC(ctor, {
      name: wrapName('branch')(ctor),
      render: function render(h) {
        if (testFn.call(this, this.$props)) {
          return trueFn.call(this, h);
        } else {
          return falseFn.call(this, h);
        }
      }
    });
  };
});

var mapProps = (function (mapper) {
  var options = {
    computed: {
      mapProps: function mapProps() {
        return mapper.call(this, this.$props);
      }
    }
  };
  var renderWith = {
    props: function props() {
      return this.mapProps;
    }
  };
  return function (ctor) {
    var hoc = createHOC(ctor, options, renderWith);
    hoc.name = wrapName('mapProps')(hoc);
    return hoc;
  };
});

// IE9-11 do not support `Object.assign`
var poly = function poly(target) {
  if (target == null) {
    throw new TypeError('Uncaught TypeError: Cannot convert undefined or null to object');
  }

  for (var i = 0, il = arguments.length <= 1 ? 0 : arguments.length - 1; i < il; i += 1) {
    var source = arguments.length <= i + 1 ? undefined : arguments[i + 1];
    if (source == null) {
      continue;
    }

    for (var key in source) {
      if (Object.hasOwnProperty.call(source, key)) {
        Object.defineProperty(target, key, {
          enumerable: true,
          writable: true,
          value: source[key]
        });
      }
    }
  }

  // $FlowFixMe
  return target;
};

var assign = Object.assign || poly;

var withPropsFn = function withPropsFn(ctor, mapper) {
  return mapProps(function (props) {
    var mapped = mapper.call(this, props);
    return assign({}, props, mapped);
  })(ctor);
};

var withComputedProps = function withComputedProps(ctor, keys, allProps) {
  var computed = {};
  var props = {};
  keys.forEach(function (key) {
    var value = allProps[key];
    if (typeof value === 'function') {
      var computedName = 'computed_' + key;
      computed[computedName] = value;
      value = function value() {
        return this[computedName];
      };
    }
    props[key] = value;
  });
  var hoc = createHOC(ctor, { computed: computed }, { props: props });
  keys.forEach(function (key) {
    if (hoc.props[key] && hoc.props[key].required) {
      hoc.props[key] = assign(hoc.props[key], { required: false });
    }
  });
  return hoc;
};

var withStaticProps = function withStaticProps(ctor, props) {
  var hoc = createHOC(ctor, null, { props: props });
  Object.keys(props).forEach(function (key) {
    if (hoc.props[key] && hoc.props[key].required) {
      hoc.props[key] = assign(hoc.props[key], { required: false });
    }
  });
  return hoc;
};

var getHoc = function getHoc(mapper, ctor) {
  if (typeof mapper === 'function') {
    return withPropsFn(ctor, mapper);
  }
  var keys = Object.keys(mapper);
  if (keys.some(function (key) {
    return typeof mapper[key] === 'function';
  })) {
    return withComputedProps(ctor, keys, mapper);
  }
  return withStaticProps(ctor, mapper);
};

var withProps = function withProps(mapper) {
  return function (ctor) {
    var hoc = getHoc(mapper, ctor);
    hoc.name = wrapName('withProps')(ctor);
    return hoc;
  };
};

var defaultProps = function defaultProps(defaults) {
  return function (ctor) {
    var keys = Object.keys(defaults);

    var hoc = mapProps(function (props) {
      var result = assign({}, props);
      keys.forEach(function (key) {
        if (result[key] === undefined) {
          result[key] = defaults[key];
        }
      });
      return result;
    })(ctor);
    hoc.name = wrapName('defaultProps')(ctor);
    keys.forEach(function (key) {
      if (hoc.props[key] && hoc.props[key].required) {
        hoc.props[key] = assign(hoc.props[key], { required: false });
      }
    });
    return hoc;
  };
};

var acceptProps = (function (props) {
  if (typeof props === 'string') {
    props = [props];
  }
  return function (ctor) {
    return createHOC(ctor, {
      props: props,
      name: wrapName('acceptProps')(ctor)
    });
  };
});

var withHandlers = function withHandlers(handlers) {
  var listeners = {};
  var methods = {};

  Object.keys(handlers).forEach(function (key) {
    var methodName = 'handle' + key.charAt(0).toUpperCase() + key.substr(1);
    methods[methodName] = handlers[key];
    listeners[key] = function () {
      return this[methodName].apply(this, arguments);
    };
  });

  return function (ctor) {
    return createHOC(ctor, {
      name: wrapName('withHandlers')(ctor),
      methods: methods
    }, {
      listeners: listeners
    });
  };
};

var withNativeHandlers = function withNativeHandlers(handlers) {
  var listeners = {};
  var methods = {};

  Object.keys(handlers).forEach(function (key) {
    var methodName = 'handle' + key.charAt(0).toUpperCase() + key.substr(1);
    methods[methodName] = handlers[key];
    listeners[key] = function () {
      return this[methodName].apply(this, arguments);
    };
  });

  return function (ctor) {
    return createHOC(ctor, {
      name: wrapName('withNativeHandlers')(ctor),
      methods: methods
    }, {
      nativeOn: listeners
    });
  };
};

var withPassive = function withPassive(passives) {
  var handlers = {};
  Object.keys(passives).forEach(function (key) {
    handlers[key] = function () {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      passives[key].apply(this, args);
      this.$emit.apply(this, [key].concat(args));
    };
  });

  return function (ctor) {
    var hoc = withHandlers(handlers)(ctor);
    hoc.name = wrapName('withPassive')(ctor);

    return hoc;
  };
};

var withHooks = function withHooks(hooks) {
  return function (ctor) {
    var definiteHooks = {
      name: wrapName('withHooks')(ctor)
    };
    Object.keys(hooks).forEach(function (key) {
      var value = hooks[key];
      if (typeof value === 'function') {
        definiteHooks[key] = value;
      }
    });
    return createHOC(ctor, definiteHooks);
  };
};

var withClass = (function (classes) {
  return function (ctor) {
    return createHOC(ctor, {
      name: wrapName('withClass')(ctor)
    }, {
      class: classes
    });
  };
});

var withStyle = function withStyle(styles) {
  return function (ctor) {
    return createHOC(ctor, {
      name: wrapName('withStyle')(ctor)
    }, {
      style: styles
    });
  };
};

var withData = function withData(data) {
  var keys = Object.keys(data);
  var listeners = {};

  var dataCreator = function dataCreator() {
    var _this = this;

    var result = {};
    keys.forEach(function (key) {
      var config = data[key];
      var initialValue = config.initialValue;

      if (Object.hasOwnProperty.call(config, 'initialValue')) {
        if (typeof initialValue === 'function') {
          result[key] = initialValue.call(_this, _this.$props);
        } else {
          result[key] = initialValue;
        }
      } else {
        result[key] = undefined;
      }
    });
    return result;
  };

  var propsCreator = function propsCreator(ownerProps) {
    var _this2 = this;

    var result = assign({}, ownerProps);
    keys.forEach(function (key) {
      var propName = data[key].prop || key;
      result[propName] = _this2[key];
    });
    return result;
  };

  keys.forEach(function (key) {
    var listenerName = data[key].listener || key;

    listeners[listenerName] = data[key].handler || function (value) {
      this[key] = value;
    };
  });

  return function (ctor) {
    var hoc = createHOC(ctor, {
      data: dataCreator,
      name: wrapName('withData')(ctor)
    }, {
      listeners: listeners,
      props: propsCreator
    });

    keys.forEach(function (key) {
      if (hoc.props[key]) {
        delete hoc.props[key];
      }
    });

    return hoc;
  };
};

var provide = function provide(_provide) {
  return function (ctor) {
    var hoc = createHOC(ctor, {
      provide: _provide
    });
    hoc.name = wrapName('provide')(ctor);
    return hoc;
  };
};

var getMixins = (function (ctor) {
  var mixins = void 0;
  if (ctor.options) {
    if (!ctor.options.mixins) {
      ctor.options.mixins = mixins = [];
    } else {
      mixins = ctor.options.mixins;
    }
  } else {
    if (!ctor.mixins) {
      ctor.mixins = mixins = [];
    } else {
      mixins = ctor.mixins;
    }
  }
  return mixins;
});

var withComputed = function withComputed(computed) {
  return function (ctor) {
    var mixins = getMixins(ctor);
    mixins.push({
      computed: computed
    });
    return ctor;
  };
};

var withMethods = function withMethods(methods) {
  return function (ctor) {
    var mixins = getMixins(ctor);
    mixins.push({
      methods: methods
    });
    return ctor;
  };
};

var inject = function inject(_inject) {
  return function (ctor) {
    var target = typeof ctor === 'function' ? ctor.options : ctor;

    target.mixins = (target.mixins || []).concat({
      inject: _inject
    });

    return target;
  };
};

var createSink = function createSink(fn) {
  return {
    name: 'Sink',
    render: function render() {
      fn.call(this, this.$attrs);
    }
  };
};

var componentFromProp = function componentFromProp(propName) {
  return {
    name: 'ComponentFromProp',
    props: defineProperty({}, propName, {
      type: [String, Object, Function],
      required: true
    }),
    render: function render(h) {
      var C = this[propName];
      return createRenderFn(C).call(this, h);
    }
  };
};

var componentFromSlot = function componentFromSlot() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  return assign({
    name: 'ComponentFromSlot',
    render: function render() {
      var props = assign({}, this.$attrs, this.$props);
      var listeners = this.$listeners || {};
      var vNode = void 0;

      if (this.$scopedSlots.children) {
        vNode = this.$scopedSlots.children(props);
      }
      if (this.$scopedSlots.default) {
        vNode = this.$scopedSlots.default(props)[0];
      }
      if (this.$slots.default) {
        var slot = this.$slots.default[0];
        var _options = slot.componentOptions || {};
        var propsData = _options.propsData = _options.propsData || {};
        Object.assign(propsData, props);
        vNode = slot;
      }

      if (!vNode) {
        throw new Error('No slot content for ComponentFromSlot component');
      }

      var options = vNode.componentOptions || {};
      var listenerData = options.listeners = options.listeners || {};
      Object.keys(listeners).forEach(function (key) {
        if (listenerData[key] == null) {
          listenerData[key] = listeners[key];
        } else if (Array.isArray(listenerData[key])) {
          listenerData[key].push(listeners[key]);
        } else {
          listenerData[key] = [listenerData[key], listeners[key]];
        }
      });

      return vNode;
    }
  }, options);
};

var RenderNothing = createSink(function () {});

var pipe = function pipe() {
  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return function (Component) {
    return args.reduce(function (result, fn) {
      return fn(result);
    }, Component);
  };
};

var compose = function compose() {
  for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  return pipe.apply(null, args.reverse());
};

var index = {
  mapProps: mapProps,
  withProps: withProps,
  defaultProps: defaultProps,
  acceptProps: acceptProps,
  withHandlers: withHandlers,
  withNativeHandlers: withNativeHandlers,
  withPassive: withPassive,
  withHooks: withHooks,
  withClass: withClass,
  withStyle: withStyle,
  withData: withData,

  withComputed: withComputed,
  withMethods: withMethods,
  setName: setName,

  createSink: createSink,
  componentFromProp: componentFromProp,
  componentFromSlot: componentFromSlot,
  compose: compose,
  pipe: pipe
};

export default index;
export { branch, mapProps, withProps, defaultProps, acceptProps, withHandlers, withNativeHandlers, withPassive, withHooks, withClass, withStyle, withData, provide, withComputed, withMethods, setName, inject, createSink, componentFromProp, componentFromSlot, RenderNothing as renderNothing, compose, pipe };
